/*
 * Smores Follow Along carousel
 *
 * Uses direct scrollLeft math instead of IntersectionObserver/scrollIntoView.
 * On every scroll we pick the card whose left edge is closest to the
 * track's left edge — that's the "active" card. Prev/next clamp at the
 * edges (no disabled state, no stuck slider).
 *
 * Per-card video support: the play button is the trigger; on click the
 * <video> element starts playing (and the play button hides). Clicking
 * an already-playing video toggles pause/play.
 */
class SmoresFollow extends HTMLElement {
  connectedCallback() {
    this.track = this.querySelector('.smores-follow__track');
    this.cards = [...this.querySelectorAll('.smores-follow__card')];
    this.dots = [...this.querySelectorAll('.smores-follow__dot')];
    this.prev = this.querySelector('.smores-follow__arrow--prev');
    this.next = this.querySelector('.smores-follow__arrow--next');
    if (!this.track || this.cards.length === 0) return;

    this.dots.forEach((dot, i) => dot.addEventListener('click', () => this.go(i)));
    if (this.prev) this.prev.addEventListener('click', () => this.step(-1));
    if (this.next) this.next.addEventListener('click', () => this.step(1));

    this.track.addEventListener('scroll', () => this.updateActive(), { passive: true });

    // Video play buttons
    this.querySelectorAll('[data-smores-play]').forEach((btn) => {
      btn.addEventListener('click', (e) => this.handlePlay(e, btn));
    });

    // Tapping a playing video toggles pause/play
    this.querySelectorAll('[data-smores-video]').forEach((video) => {
      video.addEventListener('click', () => {
        if (video.paused) video.play(); else video.pause();
      });
      video.addEventListener('pause', () => {
        const card = video.closest('.smores-follow__card');
        const playBtn = card?.querySelector('[data-smores-play]');
        if (playBtn) playBtn.removeAttribute('hidden');
      });
    });

    this.updateActive();
  }

  step(delta) {
    const current = this.currentIndex();
    const target = Math.max(0, Math.min(this.cards.length - 1, current + delta));
    this.go(target);
  }

  go(i) {
    const target = this.cards[Math.max(0, Math.min(this.cards.length - 1, i))];
    if (!target) return;
    const left = target.offsetLeft - this.track.offsetLeft;
    this.track.scrollTo({ left, behavior: 'smooth' });
  }

  currentIndex() {
    const scrollLeft = this.track.scrollLeft;
    let closest = 0;
    let minDist = Infinity;
    this.cards.forEach((card, i) => {
      const dist = Math.abs(card.offsetLeft - this.track.offsetLeft - scrollLeft);
      if (dist < minDist) { minDist = dist; closest = i; }
    });
    return closest;
  }

  updateActive() {
    const i = this.currentIndex();
    this.dots.forEach((dot, di) => dot.setAttribute('aria-current', String(di === i)));
  }

  handlePlay(e, btn) {
    e.preventDefault();
    e.stopPropagation();
    const card = btn.closest('.smores-follow__card');
    const video = card?.querySelector('[data-smores-video]');
    if (!video) return;
    video.controls = true;
    video.play();
    btn.setAttribute('hidden', '');
  }
}

if (!customElements.get('smores-follow')) {
  customElements.define('smores-follow', SmoresFollow);
}